/**
 * Wave 1u9j — Shared questionnaire visibility / progress engine.
 *
 * Mirrors the logic embedded in QuestionnairePublic.jsx so all dialogs
 * (Settings builder, Company tab editor, public learner/client pages) use the
 * exact same rules for show_if / goto_per_option.
 *
 * Schema:
 *   question.show_if = { mode: 'AND'|'OR', conditions: [{question_id, op, value}] }
 *   question.show_if (legacy) = { question_id, equals }
 *   question.goto_per_option = { option_value: target_question_id | '__end__' }
 */

const isEmpty = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

export function evalCondition(cond, answers) {
  const ref = answers[cond.question_id];
  const target = cond.value;
  const op = cond.op || 'equals';
  if (ref === undefined || ref === null || ref === '') return op === 'not_equals';
  const arr = Array.isArray(ref) ? ref : [ref];
  switch (op) {
    case 'equals':     return arr.some((v) => String(v) === String(target));
    case 'not_equals': return !arr.some((v) => String(v) === String(target));
    case 'gte': {
      const n = Number(ref); const t = Number(target);
      return Number.isFinite(n) && Number.isFinite(t) && n >= t;
    }
    case 'lte': {
      const n = Number(ref); const t = Number(target);
      return Number.isFinite(n) && Number.isFinite(t) && n <= t;
    }
    case 'in': {
      const targets = Array.isArray(target) ? target : String(target).split(',').map((s) => s.trim());
      return arr.some((v) => targets.includes(String(v)));
    }
    default: return false;
  }
}

export function matchesShowIf(question, answers) {
  const showIf = question.show_if;
  if (!showIf) return true;
  if (Array.isArray(showIf.conditions) && showIf.conditions.length > 0) {
    const mode = showIf.mode || 'AND';
    const evals = showIf.conditions.map((c) => evalCondition(c, answers));
    return mode === 'OR' ? evals.some(Boolean) : evals.every(Boolean);
  }
  if (showIf.question_id) {
    return evalCondition({ question_id: showIf.question_id, op: 'equals', value: showIf.equals }, answers);
  }
  return true;
}

export function computeSkipped(questions, answers) {
  const set = new Set();
  if (!questions || !questions.length) return set;
  questions.forEach((q, idx) => {
    const gpo = q.goto_per_option;
    if (!gpo) return;
    // Wave 1u9k — multi answers come as arrays; goto only branches on a
    // single concrete value.
    let ans = answers[q.id];
    if (Array.isArray(ans)) ans = ans.length === 1 ? ans[0] : null;
    if (ans === undefined || ans === null || typeof ans === 'object') return;
    const goto = gpo[ans] || gpo[String(ans)];
    if (!goto) return;
    const targetIdx = goto === '__end__'
      ? questions.length
      : questions.findIndex((x) => x.id === goto);
    if (targetIdx === -1) return;
    const stopAt = targetIdx;
    for (let i = idx + 1; i < stopAt; i++) set.add(questions[i].id);
  });
  return set;
}

export function isQuestionVisible(question, questions, answers) {
  const skipped = computeSkipped(questions, answers);
  if (skipped.has(question.id)) return false;
  return matchesShowIf(question, answers);
}

export function visibleQuestions(questions, answers) {
  const skipped = computeSkipped(questions, answers);
  return (questions || []).filter((q) => !skipped.has(q.id) && matchesShowIf(q, answers));
}

/**
 * Returns a completion summary aware of show_if / goto rules.
 */
export function computeCompletion(questions, answers) {
  const visibles = visibleQuestions(questions, answers);
  const required = visibles.filter((q) => q.required);
  const optional = visibles.filter((q) => !q.required);
  const reqDone = required.filter((q) => !isEmpty(answers[q.id])).length;
  const optDone = optional.filter((q) => !isEmpty(answers[q.id])).length;
  const totalRequired = required.length;
  const totalVisible = visibles.length;
  const totalQuestions = (questions || []).length;
  const pct = totalRequired > 0
    ? Math.round((reqDone / totalRequired) * 100)
    : (totalVisible > 0 ? Math.round(((reqDone + optDone) / totalVisible) * 100) : 0);
  const allRequiredOk = totalRequired === 0 ? (reqDone + optDone) > 0 : reqDone === totalRequired;
  const missingRequired = required.filter((q) => isEmpty(answers[q.id])).map((q) => q.label || q.id);
  return { pct, reqDone, optDone, totalRequired, totalVisible, totalQuestions, allRequiredOk, missingRequired };
}
